<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisMoneySnapshot from 'dashboard/components-next/LaisMoneySnapshot.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import FinanceiroAPI from 'dashboard/api/financeiro';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useTracking } from 'dashboard/composables/useTracking';
import CommissionAgreementsPanel from '../components/CommissionAgreementsPanel.vue';
import {
  COMMISSION_GROUP_BYS,
  COMMISSION_SOURCE_TYPES,
  COMMISSION_STATUSES,
  COMMISSION_TRACKING_EVENTS,
  activeSourceCount,
  attainmentBarClass,
  attainmentWidth,
  commissionRowKey,
  commissionTrackingPayload,
  currentMonthKey,
  canMarkPoolPaid,
  canMarkPoolPayable,
  formatCentsBRL,
  formatAttainment,
  formatBRL,
  isBehindTarget,
  normalizeReconciliation,
  normalizeReport,
  settlementStatusTone,
  settlementPendingKey,
  sourceBreakdownEntries,
} from '../utils/commissionReport';

const { t } = useI18n();
const { track } = useTracking();
const store = useStore();
const accountAgents = useMapGetter('agents/getAgents');
const accountTeams = useMapGetter('teams/getTeams');

const period = ref(currentMonthKey());
const groupBy = ref('agent');
const sourceType = ref('');
const status = ref('');
const search = ref('');

const report = ref(normalizeReport());
const reconciliation = ref(normalizeReconciliation());
const isLoading = ref(false);
const isForbidden = ref(false);
const errorMessage = ref('');
const targetModal = ref(null);
const targetValue = ref(0);
const expandedRows = ref(new Set());
const pendingPeriodAction = ref(null);
const periodActionLoading = ref(false);
const periodActionReason = ref('');
const periodActionError = ref('');
const settlementLoadingKey = ref('');
const settlementErrors = ref({});
const settlementIdempotencyKeys = ref({});
const paymentReferences = ref({});
const manualFieldErrors = ref({});
const manualErrorMessage = ref('');
const showCommissionAgreements = ref(false);
const commissionAgreementsLabel = computed(() =>
  t('COMMISSION_MANAGEMENT.ACTIONS.AGREEMENTS')
);

// FE-FIN-01 — Lançamento manual de comissão (POST financeiro/comissoes/manual).
// Contrato do controller: `period` no nível superior e atributos em `comissao`.
// `source_type: 'manual'`, `agency_id` e trilha de auditoria são preenchidos no backend.
const MANUAL_BUSINESS_CONTEXTS = ['purchase', 'rental'];
const MANUAL_MIN_REASON_LENGTH = 5;
const MANUAL_MAX_REASON_LENGTH = 500;
const MANUAL_PERIOD_PATTERN = /^\d{4}-\d{2}$/;
const MANUAL_STATUS_TONES = Object.freeze({
  pending: 'warning',
  confirmed: 'positive',
  paid: 'positive',
  canceled: 'danger',
});
const showManualModal = ref(false);
const isSubmittingManual = ref(false);
const emptyManualForm = () => ({
  agent_id: '',
  team_id: '',
  business_context: '',
  value: '',
  base_amount: '',
  percentage: '',
  status: 'confirmed',
  reason: '',
  period: period.value,
});
const manualForm = ref(emptyManualForm());

const filtros = computed(() => report.value.filtros);
const totais = computed(() => report.value.totais);
const breakdown = computed(() => report.value.breakdown);
const rollup = computed(() => report.value.rollup);
const periodo = computed(() => report.value.periodo);
const isOwnOnly = computed(() => Boolean(filtros.value.somente_proprio));
const reconciliationSummary = computed(() => reconciliation.value.resumo);
const reconciliationPools = computed(() => reconciliation.value.pools || []);
const reconciliationIssueEntries = computed(() =>
  Object.entries(reconciliationSummary.value.issue_counts || {}).filter(
    ([, count]) => Number(count) > 0
  )
);
const hasReconciliationIssues = computed(
  () => Number(reconciliationSummary.value.unbalanced_pools || 0) > 0
);
// Gestão (fechar/exportar consolidado/meta) só para quem o backend autoriza como
// gestor — quando a resposta vem escopada para "somente_proprio", o usuário é corretor.
const canManage = computed(() => !isLoading.value && !isOwnOnly.value);
const isPeriodClosed = computed(() => Boolean(periodo.value.fechado));
const isPeriodReopened = computed(() => Boolean(periodo.value.reaberto));
const canEditPeriod = computed(() => canManage.value && !isPeriodClosed.value);

const manualAgentOptions = computed(() =>
  (accountAgents.value || [])
    .filter(agent => agent?.id)
    .map(agent => ({ value: agent.id, label: agent.name || agent.email }))
);
const manualTeamOptions = computed(() =>
  (accountTeams.value || [])
    .filter(team => team?.id)
    .map(team => ({ value: team.id, label: team.name }))
);
const manualNumberValue = field => {
  const rawValue = manualForm.value[field];
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return null;
  }
  const parsed = Number(rawValue);
  return Number.isFinite(parsed) ? parsed : null;
};
const manualValuePreview = computed(() => manualNumberValue('value'));
const manualBaseAmountPreview = computed(() =>
  manualNumberValue('base_amount')
);
const manualPercentagePreview = computed(() => manualNumberValue('percentage'));
const hasManualBeneficiary = computed(() =>
  Boolean(manualForm.value.agent_id || manualForm.value.team_id)
);
const isManualPeriodValid = computed(() =>
  MANUAL_PERIOD_PATTERN.test(manualForm.value.period || '')
);
const isManualReasonValid = computed(() => {
  const length = manualForm.value.reason.trim().length;
  return (
    length >= MANUAL_MIN_REASON_LENGTH && length <= MANUAL_MAX_REASON_LENGTH
  );
});
const isManualBaseAmountValid = computed(
  () =>
    manualForm.value.base_amount === '' ||
    (manualBaseAmountPreview.value !== null &&
      manualBaseAmountPreview.value >= 0)
);
const isManualPercentageValid = computed(
  () =>
    manualForm.value.percentage === '' ||
    (manualPercentagePreview.value !== null &&
      manualPercentagePreview.value >= 0 &&
      manualPercentagePreview.value <= 100)
);
const isManualBusinessContextValid = computed(
  () =>
    !manualForm.value.business_context ||
    MANUAL_BUSINESS_CONTEXTS.includes(manualForm.value.business_context)
);
const isManualStatusValid = computed(() =>
  COMMISSION_STATUSES.includes(manualForm.value.status)
);
const isManualValid = computed(
  () =>
    manualValuePreview.value !== null &&
    manualValuePreview.value > 0 &&
    hasManualBeneficiary.value &&
    isManualPeriodValid.value &&
    isManualReasonValid.value &&
    isManualBaseAmountValid.value &&
    isManualPercentageValid.value &&
    isManualBusinessContextValid.value &&
    isManualStatusValid.value
);
const manualStatusTone = computed(
  () => MANUAL_STATUS_TONES[manualForm.value.status] || 'neutral'
);
const isEmpty = computed(
  () => !isLoading.value && !errorMessage.value && breakdown.value.length === 0
);
const periodActionModal = computed(() => {
  const isClose = pendingPeriodAction.value === 'close';
  return {
    title: isClose
      ? t('COMMISSION_MANAGEMENT.ACTIONS.CLOSE_PERIOD')
      : t('COMMISSION_MANAGEMENT.ACTIONS.REOPEN_PERIOD'),
    description: isClose
      ? t('COMMISSION_MANAGEMENT.CONFIRM_CLOSE')
      : t('COMMISSION_MANAGEMENT.CONFIRM_REOPEN'),
    confirmText: isClose
      ? t('COMMISSION_MANAGEMENT.ACTIONS.CLOSE_PERIOD')
      : t('COMMISSION_MANAGEMENT.ACTIONS.REOPEN_PERIOD'),
    confirmVariant: isClose ? 'danger' : 'primary',
  };
});
const periodActionRequiresReason = computed(
  () => pendingPeriodAction.value === 'reopen'
);

const groupByOptions = computed(() =>
  COMMISSION_GROUP_BYS.map(value => ({
    value,
    label: t(`COMMISSION_MANAGEMENT.GROUP_BYS.${value}`),
  }))
);
const sourceOptions = computed(() => [
  { value: '', label: t('COMMISSION_MANAGEMENT.SOURCE_TYPES.all') },
  ...COMMISSION_SOURCE_TYPES.map(value => ({
    value,
    label: t(`COMMISSION_MANAGEMENT.SOURCE_TYPES.${value}`),
  })),
]);
const statusOptions = computed(() => [
  { value: '', label: t('COMMISSION_MANAGEMENT.STATUSES.all') },
  ...COMMISSION_STATUSES.map(value => ({
    value,
    label: t(`COMMISSION_MANAGEMENT.STATUSES.${value}`),
  })),
]);

const summaryCards = computed(() => [
  {
    key: 'total',
    label: t('COMMISSION_MANAGEMENT.SUMMARY.TOTAL'),
    value: formatBRL(totais.value.valor),
  },
  {
    key: 'target',
    label: t('COMMISSION_MANAGEMENT.SUMMARY.TARGET'),
    value: formatBRL(totais.value.meta),
  },
  {
    key: 'attainment',
    label: t('COMMISSION_MANAGEMENT.SUMMARY.ATTAINMENT'),
    value: formatAttainment(totais.value.atingimento),
  },
  {
    key: 'unassigned',
    label: t('COMMISSION_MANAGEMENT.SUMMARY.UNASSIGNED'),
    value: formatBRL(totais.value.nao_atribuida),
  },
  {
    key: 'count',
    label: t('COMMISSION_MANAGEMENT.SUMMARY.COUNT'),
    value: totais.value.qtd,
  },
]);

const queryParams = () => ({
  period: period.value,
  group_by: groupBy.value,
  source_type: sourceType.value || undefined,
  status: status.value || undefined,
  q: search.value || undefined,
});

const fetchReport = async () => {
  isLoading.value = true;
  errorMessage.value = '';
  isForbidden.value = false;
  try {
    const { data } = await FinanceiroAPI.comissoes(queryParams());
    report.value = normalizeReport(data);
    if (isOwnOnly.value) {
      reconciliation.value = normalizeReconciliation();
    } else {
      const { data: reconciliationData } =
        await FinanceiroAPI.comissoesReconciliacao({ period: period.value });
      reconciliation.value = normalizeReconciliation(reconciliationData);
    }
    track(
      COMMISSION_TRACKING_EVENTS.viewed,
      commissionTrackingPayload(report.value)
    );
  } catch (error) {
    if (error?.response?.status === 403) {
      isForbidden.value = true;
    } else {
      errorMessage.value = t('COMMISSION_MANAGEMENT.STATES.ERROR');
    }
    reconciliation.value = normalizeReconciliation();
  } finally {
    isLoading.value = false;
  }
};

const toggleRow = row => {
  const key = commissionRowKey(row);
  const next = new Set(expandedRows.value);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  expandedRows.value = next;
};

const isRowExpanded = row => expandedRows.value.has(commissionRowKey(row));

const closePeriod = () => {
  pendingPeriodAction.value = 'close';
  periodActionReason.value = '';
  periodActionError.value = '';
};

const reopenPeriod = () => {
  pendingPeriodAction.value = 'reopen';
  periodActionReason.value = '';
  periodActionError.value = '';
};

const closePeriodActionModal = () => {
  if (periodActionLoading.value) return;
  pendingPeriodAction.value = null;
  periodActionReason.value = '';
  periodActionError.value = '';
};

const confirmPeriodAction = async () => {
  const action = pendingPeriodAction.value;
  if (!action) return;
  if (action === 'reopen' && !periodActionReason.value.trim()) {
    periodActionError.value = t('COMMISSION_MANAGEMENT.REOPEN_REASON.ERROR');
    return;
  }

  periodActionLoading.value = true;
  try {
    if (action === 'close') {
      await FinanceiroAPI.fecharComissoes({ period: period.value });
    } else {
      await FinanceiroAPI.reabrirComissoes({
        period: period.value,
        reason: periodActionReason.value.trim(),
      });
    }
    track(
      action === 'close'
        ? COMMISSION_TRACKING_EVENTS.closed
        : COMMISSION_TRACKING_EVENTS.reopened,
      {
        period: period.value,
        action: action === 'close' ? 'closed' : 'reopened',
      }
    );
    useAlert(
      t(
        action === 'close'
          ? 'COMMISSION_MANAGEMENT.TOASTS.CLOSED'
          : 'COMMISSION_MANAGEMENT.TOASTS.REOPENED'
      )
    );
    pendingPeriodAction.value = null;
    periodActionReason.value = '';
    periodActionError.value = '';
    await fetchReport();
  } catch (error) {
    if (error?.response?.data?.error === 'reopen_reason_required') {
      periodActionError.value = t('COMMISSION_MANAGEMENT.REOPEN_REASON.ERROR');
    } else {
      useAlert(t('COMMISSION_MANAGEMENT.TOASTS.ERROR'));
    }
  } finally {
    periodActionLoading.value = false;
  }
};

const settlementActionKey = (pool, action) => `${pool.id}:${action}`;

const settlementIdempotencyKey = (pool, action) => {
  const key = settlementActionKey(pool, action);
  if (!settlementIdempotencyKeys.value[key]) {
    const randomPart =
      window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
    settlementIdempotencyKeys.value = {
      ...settlementIdempotencyKeys.value,
      [key]: `financeiro:comissoes:${action}:${pool.id}:${randomPart}`,
    };
  }
  return settlementIdempotencyKeys.value[key];
};

const setSettlementError = (pool, message = '') => {
  settlementErrors.value = {
    ...settlementErrors.value,
    [pool.id]: message,
  };
};

const settlementErrorFor = pool => settlementErrors.value[pool.id] || '';

const isSettlementLoading = (pool, action) =>
  settlementLoadingKey.value === settlementActionKey(pool, action);

const settlementErrorMessage = error => {
  const code = error?.response?.data?.error;
  if (code === 'commission_pool_unbalanced') {
    return t('COMMISSION_MANAGEMENT.SETTLEMENT.ERRORS.UNBALANCED');
  }
  if (code === 'invalid_settlement_state') {
    return t('COMMISSION_MANAGEMENT.SETTLEMENT.ERRORS.INVALID_STATE');
  }
  if (code === 'payment_reference_required') {
    return t('COMMISSION_MANAGEMENT.SETTLEMENT.ERRORS.PAYMENT_REFERENCE');
  }
  if (code === 'commission_period_closed') {
    return t('COMMISSION_MANAGEMENT.SETTLEMENT.ERRORS.PERIOD_CLOSED');
  }
  if (error?.response?.status === 403) {
    return t('COMMISSION_MANAGEMENT.SETTLEMENT.ERRORS.PERMISSION');
  }
  return t('COMMISSION_MANAGEMENT.SETTLEMENT.ERRORS.GENERIC');
};

const markPoolPayable = async pool => {
  if (!canManage.value || isPeriodClosed.value || !canMarkPoolPayable(pool)) {
    return;
  }

  settlementLoadingKey.value = settlementActionKey(pool, 'payable');
  setSettlementError(pool);
  try {
    const { data } = await FinanceiroAPI.marcarComissaoPayable(pool.id, {
      idempotency_key: settlementIdempotencyKey(pool, 'payable'),
    });
    track(COMMISSION_TRACKING_EVENTS.settlementMarked, {
      action: 'payable',
      pool_id: pool.id,
      status: data?.pool?.status,
      idempotent: Boolean(data?.idempotent),
    });
    useAlert(t('COMMISSION_MANAGEMENT.SETTLEMENT.TOASTS.PAYABLE'));
    await fetchReport();
  } catch (error) {
    setSettlementError(pool, settlementErrorMessage(error));
  } finally {
    settlementLoadingKey.value = '';
  }
};

const markPoolPaid = async pool => {
  if (!canManage.value || isPeriodClosed.value || !canMarkPoolPaid(pool)) {
    return;
  }

  settlementLoadingKey.value = settlementActionKey(pool, 'paid');
  setSettlementError(pool);
  try {
    const { data } = await FinanceiroAPI.marcarComissaoPaid(pool.id, {
      idempotency_key: settlementIdempotencyKey(pool, 'paid'),
      payment_reference: paymentReferences.value[pool.id],
    });
    paymentReferences.value = { ...paymentReferences.value, [pool.id]: '' };
    track(COMMISSION_TRACKING_EVENTS.settlementMarked, {
      action: 'paid',
      pool_id: pool.id,
      status: data?.pool?.status,
      idempotent: Boolean(data?.idempotent),
    });
    useAlert(t('COMMISSION_MANAGEMENT.SETTLEMENT.TOASTS.PAID'));
    await fetchReport();
  } catch (error) {
    setSettlementError(pool, settlementErrorMessage(error));
  } finally {
    settlementLoadingKey.value = '';
  }
};

const exportReport = view => {
  track(COMMISSION_TRACKING_EVENTS.exported, { period: period.value, view });
  window.open(
    FinanceiroAPI.comissoesExportUrl({ ...queryParams(), view }),
    '_blank',
    'noopener'
  );
};

const openTargetModal = row => {
  if (isPeriodClosed.value) return;

  targetModal.value = {
    scope_type: groupBy.value,
    scope_id: row?.agent_id || row?.team_id || row?.id,
    name: row?.agent_nome || row?.nome,
  };
  targetValue.value = row?.meta || 0;
};

const saveTarget = async () => {
  if (isPeriodClosed.value) return;

  try {
    await FinanceiroAPI.definirMetaComissao({
      period: period.value,
      meta: {
        scope_type: targetModal.value.scope_type,
        scope_id: targetModal.value.scope_id,
        target_value: targetValue.value,
      },
    });
    track(COMMISSION_TRACKING_EVENTS.targetSet, {
      scope_type: targetModal.value.scope_type,
      target_value: Number(targetValue.value),
    });
    useAlert(t('COMMISSION_MANAGEMENT.TOASTS.TARGET_SAVED'));
    targetModal.value = null;
    await fetchReport();
  } catch (error) {
    useAlert(t('COMMISSION_MANAGEMENT.TOASTS.ERROR'));
  }
};

const openManualModal = () => {
  if (!canManage.value || isPeriodClosed.value) return;

  manualForm.value = emptyManualForm();
  manualFieldErrors.value = {};
  manualErrorMessage.value = '';
  showManualModal.value = true;
  // Carrega corretores/equipes da conta para os selects (idempotente).
  if (!manualAgentOptions.value.length) store.dispatch('agents/get');
  if (!manualTeamOptions.value.length) store.dispatch('teams/get');
};

const closeManualModal = () => {
  if (isSubmittingManual.value) return;
  showManualModal.value = false;
  manualFieldErrors.value = {};
  manualErrorMessage.value = '';
};

const normalizeManualFieldErrors = rawErrors =>
  Object.entries(rawErrors || {}).reduce((acc, [key, value]) => {
    acc[key] = Array.isArray(value) ? value : [value];
    return acc;
  }, {});

const manualFieldError = key => {
  const value = manualFieldErrors.value[key];
  if (!value) return '';
  return Array.isArray(value) ? value.join(', ') : String(value);
};

const clearManualFieldError = (...keys) => {
  const nextErrors = { ...manualFieldErrors.value };
  keys.forEach(key => delete nextErrors[key]);
  manualFieldErrors.value = nextErrors;
  manualErrorMessage.value = '';
};

const setManualValidationError = (errors, key, i18nKey) => {
  errors[key] = [t(`COMMISSION_MANAGEMENT.MANUAL_MODAL.ERRORS.${i18nKey}`)];
};

const validateManualForm = () => {
  const errors = {};
  if (manualValuePreview.value === null) {
    setManualValidationError(errors, 'value', 'VALUE_REQUIRED');
  } else if (manualValuePreview.value <= 0) {
    setManualValidationError(errors, 'value', 'VALUE_POSITIVE');
  }

  if (!isManualPeriodValid.value) {
    setManualValidationError(errors, 'period', 'PERIOD_INVALID');
  }

  if (!hasManualBeneficiary.value) {
    setManualValidationError(errors, 'beneficiary', 'BENEFICIARY_REQUIRED');
  }

  if (!isManualBusinessContextValid.value) {
    setManualValidationError(
      errors,
      'business_context',
      'BUSINESS_CONTEXT_INVALID'
    );
  }

  if (
    manualForm.value.base_amount !== '' &&
    manualBaseAmountPreview.value === null
  ) {
    setManualValidationError(errors, 'base_amount', 'BASE_AMOUNT_INVALID');
  } else if (
    manualBaseAmountPreview.value !== null &&
    manualBaseAmountPreview.value < 0
  ) {
    setManualValidationError(errors, 'base_amount', 'BASE_AMOUNT_NEGATIVE');
  }

  if (
    manualForm.value.percentage !== '' &&
    manualPercentagePreview.value === null
  ) {
    setManualValidationError(errors, 'percentage', 'PERCENTAGE_INVALID');
  } else if (
    manualPercentagePreview.value !== null &&
    (manualPercentagePreview.value < 0 || manualPercentagePreview.value > 100)
  ) {
    setManualValidationError(errors, 'percentage', 'PERCENTAGE_RANGE');
  }

  if (!isManualStatusValid.value) {
    setManualValidationError(errors, 'status', 'STATUS_INVALID');
  }

  const reasonLength = manualForm.value.reason.trim().length;
  if (!reasonLength) {
    setManualValidationError(errors, 'reason', 'REASON_REQUIRED');
  } else if (reasonLength < MANUAL_MIN_REASON_LENGTH) {
    setManualValidationError(errors, 'reason', 'REASON_SHORT');
  } else if (reasonLength > MANUAL_MAX_REASON_LENGTH) {
    setManualValidationError(errors, 'reason', 'REASON_LONG');
  }

  manualFieldErrors.value = errors;
  return Object.keys(errors).length === 0;
};

const manualSubmitErrorMessage = error => {
  const code = error?.response?.data?.error;
  if (code === 'commission_period_closed') {
    return t('COMMISSION_MANAGEMENT.MANUAL_MODAL.TOASTS.PERIOD_CLOSED');
  }
  if (code === 'manual_commission_invalid') {
    return t('COMMISSION_MANAGEMENT.MANUAL_MODAL.TOASTS.ERROR_FIELDS');
  }
  if (error?.response?.status === 403) {
    return t('COMMISSION_MANAGEMENT.MANUAL_MODAL.TOASTS.PERMISSION');
  }
  return t('COMMISSION_MANAGEMENT.MANUAL_MODAL.TOASTS.ERROR');
};

const buildManualPayload = () => {
  const comissao = { value: manualValuePreview.value };
  if (manualForm.value.agent_id) comissao.agent_id = manualForm.value.agent_id;
  if (manualForm.value.team_id) comissao.team_id = manualForm.value.team_id;
  if (manualForm.value.business_context) {
    comissao.business_context = manualForm.value.business_context;
  }
  if (manualForm.value.base_amount !== '') {
    comissao.base_amount = Number(manualForm.value.base_amount);
  }
  if (manualForm.value.percentage !== '') {
    comissao.percentage = Number(manualForm.value.percentage);
  }
  if (manualForm.value.status) comissao.status = manualForm.value.status;
  comissao.reason = manualForm.value.reason.trim();
  return { period: manualForm.value.period || period.value, comissao };
};

const submitManual = async () => {
  if (isSubmittingManual.value || !canManage.value || isPeriodClosed.value) {
    return;
  }
  if (!validateManualForm()) return;

  isSubmittingManual.value = true;
  manualErrorMessage.value = '';
  try {
    const payload = buildManualPayload();
    await FinanceiroAPI.lancarComissaoManual(payload);
    track(COMMISSION_TRACKING_EVENTS.manualCreated, {
      period: payload.period,
      value: payload.comissao.value,
      has_agent: Boolean(payload.comissao.agent_id),
      status: payload.comissao.status || 'confirmed',
    });
    useAlert(t('COMMISSION_MANAGEMENT.MANUAL_MODAL.TOASTS.SUCCESS'));
    showManualModal.value = false;
    // Reflete o novo lançamento no razão: alinha o filtro ao período lançado e
    // re-consulta o relatório existente.
    period.value = payload.period;
    await fetchReport();
  } catch (error) {
    manualFieldErrors.value = normalizeManualFieldErrors(
      error?.response?.data?.field_errors
    );
    manualErrorMessage.value = manualSubmitErrorMessage(error);
    useAlert(manualErrorMessage.value);
  } finally {
    isSubmittingManual.value = false;
  }
};

const canSettlePayable = pool =>
  !isPeriodClosed.value && canMarkPoolPayable(pool);

const canSettlePaid = pool => !isPeriodClosed.value && canMarkPoolPaid(pool);

const settlementPendingMessage = pool => {
  const key = settlementPendingKey(pool);
  return key ? t(`COMMISSION_MANAGEMENT.SETTLEMENT.PENDING.${key}`) : '';
};

const formatPercent = value => {
  const number = Number(value || 0);
  return `${Number.isFinite(number) ? number.toFixed(2) : '0.00'}%`;
};

onMounted(fetchReport);
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <LaisHeader
      :title="t('COMMISSION_MANAGEMENT.TITLE')"
      :description="t('COMMISSION_MANAGEMENT.SUBTITLE')"
    />

    <div v-if="isOwnOnly" class="px-6 pt-3">
      <p class="text-sm text-n-slate-11">
        {{ t('COMMISSION_MANAGEMENT.STATES.OWN_ONLY') }}
      </p>
    </div>

    <!-- Filtros -->
    <div class="flex flex-wrap items-end gap-3 px-6 py-4">
      <label class="flex flex-col text-xs text-n-slate-11">
        {{ t('COMMISSION_MANAGEMENT.FILTERS.PERIOD') }}
        <input
          v-model="period"
          type="month"
          class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
        />
      </label>
      <label class="flex flex-col text-xs text-n-slate-11">
        {{ t('COMMISSION_MANAGEMENT.FILTERS.GROUP_BY') }}
        <select
          v-model="groupBy"
          class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
        >
          <option
            v-for="opt in groupByOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </label>
      <label class="flex flex-col text-xs text-n-slate-11">
        {{ t('COMMISSION_MANAGEMENT.FILTERS.SOURCE_TYPE') }}
        <select
          v-model="sourceType"
          class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
        >
          <option
            v-for="opt in sourceOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </label>
      <label class="flex flex-col text-xs text-n-slate-11">
        {{ t('COMMISSION_MANAGEMENT.FILTERS.STATUS') }}
        <select
          v-model="status"
          class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
        >
          <option
            v-for="opt in statusOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </label>
      <label class="flex flex-col text-xs text-n-slate-11 flex-1 min-w-[160px]">
        {{ t('COMMISSION_MANAGEMENT.FILTERS.SEARCH') }}
        <input
          v-model="search"
          type="search"
          class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
          @keyup.enter="fetchReport"
        />
      </label>
      <LaisPrimaryButton
        :label="t('COMMISSION_MANAGEMENT.FILTERS.APPLY')"
        @click="fetchReport"
      />
    </div>

    <div class="flex flex-wrap items-center gap-2 px-6 pb-2">
      <LaisPrimaryButton
        size="sm"
        variant="ghost"
        icon="i-lucide-file-signature"
        @click="showCommissionAgreements = true"
      >
        {{ commissionAgreementsLabel }}
      </LaisPrimaryButton>
    </div>

    <!-- Ações de gestão -->
    <div v-if="canManage" class="flex flex-wrap items-center gap-2 px-6 pb-2">
      <LaisPrimaryButton
        size="sm"
        icon="i-lucide-plus"
        :disabled="isPeriodClosed"
        @click="openManualModal"
      >
        {{ t('COMMISSION_MANAGEMENT.ACTIONS.MANUAL_ENTRY') }}
      </LaisPrimaryButton>
      <button
        v-if="!isPeriodClosed"
        class="rounded-lg border border-n-weak px-3 py-1.5 text-sm hover:bg-n-alpha-1"
        @click="closePeriod"
      >
        {{ t('COMMISSION_MANAGEMENT.ACTIONS.CLOSE_PERIOD') }}
      </button>
      <button
        v-else
        class="rounded-lg border border-n-weak px-3 py-1.5 text-sm hover:bg-n-alpha-1"
        @click="reopenPeriod"
      >
        {{ t('COMMISSION_MANAGEMENT.ACTIONS.REOPEN_PERIOD') }}
      </button>
      <button
        class="rounded-lg border border-n-weak px-3 py-1.5 text-sm hover:bg-n-alpha-1"
        @click="exportReport('detail')"
      >
        {{ t('COMMISSION_MANAGEMENT.ACTIONS.EXPORT_DETAIL') }}
      </button>
      <button
        class="rounded-lg border border-n-weak px-3 py-1.5 text-sm hover:bg-n-alpha-1"
        @click="exportReport('rollup')"
      >
        {{ t('COMMISSION_MANAGEMENT.ACTIONS.EXPORT_ROLLUP') }}
      </button>
    </div>

    <p
      v-if="isPeriodClosed"
      class="mx-6 mb-2 rounded-lg bg-n-amber-3 px-3 py-2 text-sm text-n-amber-11"
    >
      {{
        t('COMMISSION_MANAGEMENT.PERIOD_CLOSED', {
          date: periodo.fechado_em || '-',
          user: periodo.fechado_por || '-',
        })
      }}
    </p>
    <p
      v-else-if="isPeriodReopened"
      class="mx-6 mb-2 rounded-lg bg-n-sky-3 px-3 py-2 text-sm text-n-sky-11"
    >
      {{
        t('COMMISSION_MANAGEMENT.PERIOD_REOPENED', {
          date: periodo.reaberto_em || '-',
          user: periodo.reaberto_por || '-',
          reason: periodo.motivo_reabertura || '-',
        })
      }}
    </p>

    <!-- Estados -->
    <div
      v-if="isForbidden"
      class="flex flex-1 items-center justify-center text-n-slate-11"
    >
      {{ t('COMMISSION_MANAGEMENT.STATES.FORBIDDEN') }}
    </div>
    <div v-else-if="isLoading" class="px-6 py-10 text-center text-n-slate-11">
      {{ t('COMMISSION_MANAGEMENT.STATES.LOADING') }}
    </div>
    <div
      v-else-if="errorMessage"
      class="flex flex-col items-center gap-3 px-6 py-10 text-n-slate-11"
    >
      <p>{{ errorMessage }}</p>
      <button
        class="rounded-lg border border-n-weak px-3 py-1.5 text-sm"
        @click="fetchReport"
      >
        {{ t('COMMISSION_MANAGEMENT.STATES.RETRY') }}
      </button>
    </div>
    <div v-else-if="isEmpty" class="px-6 py-10 text-center text-n-slate-11">
      {{ t('COMMISSION_MANAGEMENT.STATES.EMPTY') }}
    </div>

    <template v-else>
      <!-- Resumo -->
      <div class="grid grid-cols-2 gap-3 px-6 py-2 md:grid-cols-5">
        <LaisCard v-for="card in summaryCards" :key="card.key" class="p-4">
          <p class="text-xs text-n-slate-11">{{ card.label }}</p>
          <p class="mt-1 text-lg font-semibold text-n-slate-12">
            {{ card.value }}
          </p>
        </LaisCard>
      </div>

      <div v-if="canManage" class="px-6 py-2">
        <LaisCard
          class="p-4"
          :class="
            hasReconciliationIssues ? 'border-n-ruby-6' : 'border-n-teal-6'
          "
        >
          <div
            class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between"
          >
            <div>
              <p class="text-xs font-medium text-n-slate-11">
                {{ t('COMMISSION_MANAGEMENT.RECONCILIATION.TITLE') }}
              </p>
              <p class="mt-1 text-base font-semibold text-n-slate-12">
                {{
                  hasReconciliationIssues
                    ? t('COMMISSION_MANAGEMENT.RECONCILIATION.WITH_ISSUES')
                    : t('COMMISSION_MANAGEMENT.RECONCILIATION.BALANCED')
                }}
              </p>
            </div>
            <div class="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              <div>
                <p class="text-xs text-n-slate-11">
                  {{ t('COMMISSION_MANAGEMENT.RECONCILIATION.POOLS') }}
                </p>
                <p class="font-semibold text-n-slate-12">
                  {{ reconciliationSummary.total_pools }}
                </p>
              </div>
              <div>
                <p class="text-xs text-n-slate-11">
                  {{ t('COMMISSION_MANAGEMENT.RECONCILIATION.BALANCED_POOLS') }}
                </p>
                <p class="font-semibold text-n-teal-11">
                  {{ reconciliationSummary.balanced_pools }}
                </p>
              </div>
              <div>
                <p class="text-xs text-n-slate-11">
                  {{
                    t('COMMISSION_MANAGEMENT.RECONCILIATION.UNBALANCED_POOLS')
                  }}
                </p>
                <p
                  class="font-semibold"
                  :class="
                    hasReconciliationIssues
                      ? 'text-n-ruby-11'
                      : 'text-n-slate-12'
                  "
                >
                  {{ reconciliationSummary.unbalanced_pools }}
                </p>
              </div>
              <div>
                <p class="text-xs text-n-slate-11">
                  {{ t('COMMISSION_MANAGEMENT.RECONCILIATION.NET_AMOUNT') }}
                </p>
                <p class="font-semibold text-n-slate-12">
                  {{ formatBRL(reconciliationSummary.net_amount) }}
                </p>
              </div>
            </div>
          </div>
          <p
            v-if="reconciliationIssueEntries.length"
            class="mt-3 text-xs text-n-ruby-11"
          >
            {{
              reconciliationIssueEntries
                .map(([issue, count]) =>
                  t(`COMMISSION_MANAGEMENT.RECONCILIATION.ISSUES.${issue}`, {
                    count,
                  })
                )
                .join(' · ')
            }}
          </p>

          <div
            v-if="reconciliationPools.length"
            class="mt-4 overflow-x-auto border-t border-n-weak pt-3"
          >
            <table class="min-w-[780px] w-full text-left text-xs">
              <thead class="text-n-slate-11">
                <tr>
                  <th class="py-2 pr-3">
                    {{ t('COMMISSION_MANAGEMENT.SETTLEMENT.TABLE.POOL') }}
                  </th>
                  <th class="py-2 pr-3">
                    {{ t('COMMISSION_MANAGEMENT.SETTLEMENT.TABLE.STATUS') }}
                  </th>
                  <th class="py-2 pr-3">
                    {{ t('COMMISSION_MANAGEMENT.SETTLEMENT.TABLE.NET_AMOUNT') }}
                  </th>
                  <th class="py-2 pr-3">
                    {{ t('COMMISSION_MANAGEMENT.SETTLEMENT.TABLE.PAYABLE') }}
                  </th>
                  <th class="py-2 pr-3">
                    {{ t('COMMISSION_MANAGEMENT.SETTLEMENT.TABLE.PAID') }}
                  </th>
                  <th class="py-2 pr-3">
                    {{ t('COMMISSION_MANAGEMENT.SETTLEMENT.TABLE.ISSUES') }}
                  </th>
                  <th class="py-2 text-right">
                    {{ t('COMMISSION_MANAGEMENT.SETTLEMENT.TABLE.ACTIONS') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="pool in reconciliationPools"
                  :key="`settlement-${pool.id}`"
                  class="border-t border-n-weak align-top"
                >
                  <td class="py-2 pr-3 font-medium text-n-slate-12">
                    <span class="block">
                      {{
                        t('COMMISSION_MANAGEMENT.SETTLEMENT.POOL_REFERENCE', {
                          id: pool.id,
                        })
                      }}
                    </span>
                    <span class="text-n-slate-11">
                      {{
                        t('COMMISSION_MANAGEMENT.SETTLEMENT.SOURCE_REFERENCE', {
                          type: pool.source_type,
                          id: pool.source_id,
                        })
                      }}
                    </span>
                    <span
                      v-if="pool.agreement"
                      class="mt-1 block text-n-slate-11"
                    >
                      {{
                        t('COMMISSION_MANAGEMENT.SETTLEMENT.AGREEMENT', {
                          id: pool.agreement.id || '-',
                          version: pool.agreement.version_number || '-',
                        })
                      }}
                    </span>
                    <span
                      v-if="pool.agreement?.reason"
                      class="mt-1 block text-n-slate-11"
                    >
                      {{
                        t('COMMISSION_MANAGEMENT.SETTLEMENT.AGREEMENT_REASON', {
                          reason: pool.agreement.reason,
                        })
                      }}
                    </span>
                    <details
                      v-if="pool.splits.length || pool.ledger_entries.length"
                      class="mt-2 text-xs"
                    >
                      <summary class="cursor-pointer text-n-teal-10">
                        {{
                          t('COMMISSION_MANAGEMENT.SETTLEMENT.DETAILS.TITLE')
                        }}
                      </summary>
                      <div class="mt-2 grid gap-3 text-n-slate-11">
                        <div v-if="pool.splits.length">
                          <p class="font-medium text-n-slate-12">
                            {{
                              t(
                                'COMMISSION_MANAGEMENT.SETTLEMENT.DETAILS.SPLITS'
                              )
                            }}
                          </p>
                          <div class="mt-1 grid gap-1">
                            <p
                              v-for="split in pool.splits"
                              :key="`split-${pool.id}-${split.id}`"
                            >
                              {{
                                t(
                                  'COMMISSION_MANAGEMENT.SETTLEMENT.DETAILS.SPLIT_ROW',
                                  {
                                    role: split.role,
                                    beneficiary:
                                      split.beneficiary_label ||
                                      split.beneficiary_kind,
                                    percentage: formatPercent(split.percentage),
                                    base: formatCentsBRL(
                                      split.base_amount_cents
                                    ),
                                    amount: formatCentsBRL(split.amount_cents),
                                  }
                                )
                              }}
                            </p>
                          </div>
                        </div>
                        <div v-if="pool.ledger_entries.length">
                          <p class="font-medium text-n-slate-12">
                            {{
                              t(
                                'COMMISSION_MANAGEMENT.SETTLEMENT.DETAILS.LEDGER'
                              )
                            }}
                          </p>
                          <div class="mt-1 grid gap-1">
                            <p
                              v-for="entry in pool.ledger_entries"
                              :key="`ledger-${pool.id}-${entry.id}`"
                            >
                              {{
                                t(
                                  'COMMISSION_MANAGEMENT.SETTLEMENT.DETAILS.LEDGER_ROW',
                                  {
                                    event: entry.event_type,
                                    scope: entry.scope,
                                    amount: formatCentsBRL(entry.amount_cents),
                                    period: entry.effective_period || '-',
                                    reason:
                                      entry.metadata?.reason ||
                                      entry.metadata?.operation ||
                                      '-',
                                  }
                                )
                              }}
                            </p>
                          </div>
                        </div>
                      </div>
                    </details>
                  </td>
                  <td class="py-2 pr-3">
                    <LaisStatusBadge
                      size="sm"
                      dot
                      :tone="settlementStatusTone(pool.status)"
                      :label="
                        t(
                          `COMMISSION_MANAGEMENT.SETTLEMENT.STATUSES.${pool.status}`
                        )
                      "
                    />
                    <span
                      v-if="settlementPendingMessage(pool)"
                      class="mt-1 block text-xs text-n-amber-11"
                    >
                      {{ settlementPendingMessage(pool) }}
                    </span>
                  </td>
                  <td class="py-2 pr-3">
                    {{ formatCentsBRL(pool.net_amount_cents) }}
                  </td>
                  <td class="py-2 pr-3">
                    {{ formatCentsBRL(pool.payable_pool_cents) }}
                  </td>
                  <td class="py-2 pr-3">
                    {{ formatCentsBRL(pool.paid_pool_cents) }}
                  </td>
                  <td class="max-w-[180px] py-2 pr-3 text-n-slate-11">
                    <span v-if="pool.issues.length">
                      {{
                        pool.issues
                          .map(issue =>
                            t(
                              `COMMISSION_MANAGEMENT.RECONCILIATION.ISSUES.${issue}`,
                              { count: 1 }
                            )
                          )
                          .join(' · ')
                      }}
                    </span>
                    <span v-else>
                      {{ t('COMMISSION_MANAGEMENT.SETTLEMENT.NO_ISSUES') }}
                    </span>
                    <span
                      v-if="settlementErrorFor(pool)"
                      class="mt-1 block text-n-ruby-11"
                    >
                      {{ settlementErrorFor(pool) }}
                    </span>
                  </td>
                  <td class="py-2 text-right">
                    <div
                      v-if="canMarkPoolPayable(pool)"
                      class="flex justify-end"
                    >
                      <LaisPrimaryButton
                        size="sm"
                        icon="i-lucide-badge-dollar-sign"
                        :disabled="!canSettlePayable(pool)"
                        :is-loading="isSettlementLoading(pool, 'payable')"
                        @click="markPoolPayable(pool)"
                      >
                        {{
                          t('COMMISSION_MANAGEMENT.SETTLEMENT.ACTIONS.PAYABLE')
                        }}
                      </LaisPrimaryButton>
                    </div>
                    <div
                      v-else-if="canMarkPoolPaid(pool)"
                      class="flex min-w-[280px] justify-end gap-2"
                    >
                      <input
                        v-model="paymentReferences[pool.id]"
                        type="text"
                        autocomplete="off"
                        class="min-w-0 rounded-lg border border-n-weak bg-n-background px-2 py-1.5 text-xs"
                        :placeholder="
                          t(
                            'COMMISSION_MANAGEMENT.SETTLEMENT.PAYMENT_REFERENCE'
                          )
                        "
                      />
                      <LaisPrimaryButton
                        size="sm"
                        icon="i-lucide-receipt-text"
                        :disabled="
                          !paymentReferences[pool.id] || !canSettlePaid(pool)
                        "
                        :is-loading="isSettlementLoading(pool, 'paid')"
                        @click="markPoolPaid(pool)"
                      >
                        {{ t('COMMISSION_MANAGEMENT.SETTLEMENT.ACTIONS.PAID') }}
                      </LaisPrimaryButton>
                    </div>
                    <span v-else class="text-xs text-n-slate-11">
                      {{ t('COMMISSION_MANAGEMENT.SETTLEMENT.NO_ACTION') }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </LaisCard>
      </div>

      <!-- Breakdown por corretor (desktop tabela / mobile cards) -->
      <div class="px-6 py-3">
        <table class="hidden w-full text-sm md:table">
          <thead>
            <tr class="text-left text-n-slate-11">
              <th class="py-2">{{ t('COMMISSION_MANAGEMENT.TABLE.AGENT') }}</th>
              <th>{{ t('COMMISSION_MANAGEMENT.TABLE.TEAM') }}</th>
              <th>{{ t('COMMISSION_MANAGEMENT.TABLE.VALUE') }}</th>
              <th>{{ t('COMMISSION_MANAGEMENT.TABLE.TARGET') }}</th>
              <th>{{ t('COMMISSION_MANAGEMENT.TABLE.ATTAINMENT') }}</th>
              <th>{{ t('COMMISSION_MANAGEMENT.TABLE.COUNT') }}</th>
              <th v-if="canManage" />
            </tr>
          </thead>
          <tbody>
            <template v-for="row in breakdown" :key="commissionRowKey(row)">
              <tr
                class="border-t border-n-weak"
                :class="{ 'bg-n-ruby-2': isBehindTarget(row) }"
              >
                <td class="py-2 font-medium text-n-slate-12">
                  <button
                    type="button"
                    class="flex items-center gap-2 text-left hover:text-n-teal-10"
                    :aria-expanded="isRowExpanded(row)"
                    :aria-controls="`commission-source-${commissionRowKey(row)}`"
                    @click="toggleRow(row)"
                  >
                    <span
                      class="size-4"
                      :class="
                        isRowExpanded(row)
                          ? 'i-lucide-chevron-down'
                          : 'i-lucide-chevron-right'
                      "
                      aria-hidden="true"
                    />
                    <span>
                      {{ row.agent_nome }}
                      <span class="ml-1 text-xs font-normal text-n-slate-11">
                        {{ t('COMMISSION_MANAGEMENT.TABLE.BY_SOURCE') }}
                        {{ `(${activeSourceCount(row)})` }}
                      </span>
                    </span>
                  </button>
                </td>
                <td class="text-n-slate-11">
                  {{
                    row.team_nome || t('COMMISSION_MANAGEMENT.TABLE.NO_TEAM')
                  }}
                </td>
                <td>{{ formatBRL(row.valor) }}</td>
                <td>
                  {{
                    row.meta
                      ? formatBRL(row.meta)
                      : t('COMMISSION_MANAGEMENT.TABLE.NO_TARGET')
                  }}
                </td>
                <td>
                  <div class="flex items-center gap-2">
                    <div class="h-1.5 w-16 rounded-full bg-n-alpha-2">
                      <div
                        class="h-full rounded-full"
                        :class="attainmentBarClass(row)"
                        :style="{
                          width: `${attainmentWidth(row.atingimento)}%`,
                        }"
                      />
                    </div>
                    <span>{{ formatAttainment(row.atingimento) }}</span>
                  </div>
                </td>
                <td>{{ row.qtd }}</td>
                <td v-if="canManage" class="text-right">
                  <button
                    v-if="row.agent_id && canEditPeriod"
                    class="text-xs text-n-teal-10 hover:underline"
                    @click="openTargetModal(row)"
                  >
                    {{ t('COMMISSION_MANAGEMENT.ACTIONS.SET_TARGET') }}
                  </button>
                </td>
              </tr>
              <tr
                v-if="isRowExpanded(row)"
                :id="`commission-source-${commissionRowKey(row)}`"
                class="border-t border-n-weak bg-n-alpha-1"
              >
                <td :colspan="canManage ? 7 : 6" class="px-4 py-3">
                  <div class="grid grid-cols-4 gap-3">
                    <div
                      v-for="entry in sourceBreakdownEntries(row)"
                      :key="entry.sourceType"
                      class="rounded-lg border border-n-weak bg-n-background p-3"
                    >
                      <p class="text-xs font-medium text-n-slate-11">
                        {{
                          t(
                            `COMMISSION_MANAGEMENT.SOURCE_TYPES.${entry.sourceType}`
                          )
                        }}
                      </p>
                      <p class="mt-1 font-semibold text-n-slate-12">
                        {{ formatBRL(entry.value) }}
                      </p>
                      <p class="text-xs text-n-slate-11">
                        {{ `${entry.percentage.toFixed(1)}%` }}
                      </p>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <!-- Mobile cards -->
        <div class="flex flex-col gap-2 md:hidden">
          <LaisCard
            v-for="row in breakdown"
            :key="`m-${commissionRowKey(row)}`"
            class="p-4"
            :class="{ 'border-n-ruby-6': isBehindTarget(row) }"
          >
            <button
              type="button"
              class="flex w-full items-center justify-between gap-3 text-left"
              :aria-expanded="isRowExpanded(row)"
              :aria-controls="`commission-source-mobile-${commissionRowKey(row)}`"
              @click="toggleRow(row)"
            >
              <span>
                <span class="block font-medium text-n-slate-12">
                  {{ row.agent_nome }}
                </span>
                <span class="text-xs text-n-slate-11">
                  {{
                    row.team_nome || t('COMMISSION_MANAGEMENT.TABLE.NO_TEAM')
                  }}
                </span>
              </span>
              <span class="flex items-center gap-2">
                <span class="font-semibold">{{ formatBRL(row.valor) }}</span>
                <span
                  class="size-4 text-n-slate-11"
                  :class="
                    isRowExpanded(row)
                      ? 'i-lucide-chevron-down'
                      : 'i-lucide-chevron-right'
                  "
                  aria-hidden="true"
                />
              </span>
            </button>

            <div class="mt-3 flex items-center gap-2">
              <div class="h-1.5 flex-1 rounded-full bg-n-alpha-2">
                <div
                  class="h-full rounded-full"
                  :class="attainmentBarClass(row)"
                  :style="{ width: `${attainmentWidth(row.atingimento)}%` }"
                />
              </div>
              <span class="text-xs text-n-slate-11">
                {{ formatAttainment(row.atingimento) }}
              </span>
            </div>

            <div
              v-if="isRowExpanded(row)"
              :id="`commission-source-mobile-${commissionRowKey(row)}`"
              class="mt-3 grid gap-2"
            >
              <div
                v-for="entry in sourceBreakdownEntries(row)"
                :key="entry.sourceType"
                class="flex items-center justify-between rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2"
              >
                <span class="text-xs text-n-slate-11">
                  {{
                    t(`COMMISSION_MANAGEMENT.SOURCE_TYPES.${entry.sourceType}`)
                  }}
                </span>
                <span class="text-sm font-semibold text-n-slate-12">
                  {{ formatBRL(entry.value) }}
                  <span class="ml-1 text-xs font-normal text-n-slate-11">
                    {{ `${entry.percentage.toFixed(1)}%` }}
                  </span>
                </span>
              </div>
            </div>

            <button
              v-if="canEditPeriod && row.agent_id"
              type="button"
              class="mt-3 text-xs text-n-teal-10 hover:underline"
              @click="openTargetModal(row)"
            >
              {{ t('COMMISSION_MANAGEMENT.ACTIONS.SET_TARGET') }}
            </button>
          </LaisCard>
        </div>
      </div>

      <!-- Rollup -->
      <div v-if="rollup.length" class="px-6 pb-8">
        <h3 class="mb-2 text-sm font-semibold text-n-slate-12">
          {{
            t('COMMISSION_MANAGEMENT.ROLLUP.TITLE', {
              dimension: t(`COMMISSION_MANAGEMENT.GROUP_BYS.${groupBy}`),
            })
          }}
        </h3>
        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <LaisCard
            v-for="row in rollup"
            :key="`r-${row.dimensao}-${row.id || 'none'}`"
            class="p-4"
          >
            <div class="flex items-center justify-between">
              <p class="font-medium text-n-slate-12">{{ row.nome }}</p>
              <p class="font-semibold">{{ formatBRL(row.valor) }}</p>
            </div>
            <p class="mt-1 text-xs text-n-slate-11">
              {{
                t('COMMISSION_MANAGEMENT.ROLLUP.BROKERS', {
                  count: row.qtd_corretores,
                })
              }}
              · {{ t('COMMISSION_MANAGEMENT.TABLE.ATTAINMENT') }}:
              {{ formatAttainment(row.atingimento) }}
            </p>
          </LaisCard>
        </div>
      </div>
    </template>

    <LaisConfirmModal
      :show="!!pendingPeriodAction"
      :title="periodActionModal.title"
      :description="periodActionModal.description"
      :confirm-text="periodActionModal.confirmText"
      :confirm-variant="periodActionModal.confirmVariant"
      :is-loading="periodActionLoading"
      @close="closePeriodActionModal"
      @confirm="confirmPeriodAction"
    >
      <label
        v-if="periodActionRequiresReason"
        class="mt-4 flex flex-col gap-1 text-xs text-n-slate-11"
      >
        {{ t('COMMISSION_MANAGEMENT.REOPEN_REASON.LABEL') }}
        <textarea
          v-model="periodActionReason"
          rows="3"
          class="rounded-lais border border-lais-border bg-white px-3 py-2 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
          :placeholder="t('COMMISSION_MANAGEMENT.REOPEN_REASON.PLACEHOLDER')"
        />
      </label>
      <p v-if="periodActionError" class="mt-2 text-xs text-n-ruby-11">
        {{ periodActionError }}
      </p>
    </LaisConfirmModal>

    <!-- Modal de meta -->
    <div
      v-if="targetModal"
      class="fixed inset-0 z-50 flex items-end justify-center bg-n-alpha-black2 p-0 sm:items-center sm:p-6"
      @click.self="targetModal = null"
    >
      <div
        class="w-full max-w-md rounded-t-2xl bg-n-background p-6 sm:rounded-2xl"
      >
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ t('COMMISSION_MANAGEMENT.TARGET_MODAL.TITLE') }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">{{ targetModal.name }}</p>
        <label class="mt-4 flex flex-col text-xs text-n-slate-11">
          {{ t('COMMISSION_MANAGEMENT.TARGET_MODAL.TARGET_VALUE') }}
          <input
            v-model.number="targetValue"
            type="number"
            min="0"
            step="0.01"
            class="mt-1 rounded-lg border border-n-weak bg-n-background px-3 py-2 text-sm"
          />
        </label>
        <div class="mt-5 flex justify-end gap-2">
          <button
            class="rounded-lg border border-n-weak px-3 py-1.5 text-sm"
            @click="targetModal = null"
          >
            {{ t('COMMISSION_MANAGEMENT.TARGET_MODAL.CANCEL') }}
          </button>
          <LaisPrimaryButton
            :label="t('COMMISSION_MANAGEMENT.TARGET_MODAL.SAVE')"
            @click="saveTarget"
          />
        </div>
      </div>
    </div>

    <!-- FE-FIN-01 — Modal de lançamento manual de comissão -->
    <LaisModal
      :show="showManualModal"
      :title="t('COMMISSION_MANAGEMENT.MANUAL_MODAL.TITLE')"
      max-width-class="max-w-[620px]"
      @close="closeManualModal"
    >
      <form class="flex flex-col gap-5" @submit.prevent="submitManual">
        <p class="text-lais-body-sm text-lais-text-body">
          {{ t('COMMISSION_MANAGEMENT.MANUAL_MODAL.DESCRIPTION') }}
        </p>

        <div class="grid gap-4 sm:grid-cols-2">
          <label class="flex flex-col gap-1">
            <span class="text-lais-label-sm font-semibold text-lais-text-body">
              {{ t('COMMISSION_MANAGEMENT.MANUAL_MODAL.FIELDS.VALUE') }}
            </span>
            <input
              v-model="manualForm.value"
              type="number"
              min="0"
              step="0.01"
              inputmode="decimal"
              required
              :aria-invalid="Boolean(manualFieldError('value'))"
              class="rounded-lais border border-lais-border bg-white px-3 py-2 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
              @input="clearManualFieldError('value')"
            />
            <p
              v-if="manualFieldError('value')"
              class="text-lais-body-xs text-n-ruby-11"
            >
              {{ manualFieldError('value') }}
            </p>
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-lais-label-sm font-semibold text-lais-text-body">
              {{ t('COMMISSION_MANAGEMENT.MANUAL_MODAL.FIELDS.PERIOD') }}
            </span>
            <input
              v-model="manualForm.period"
              type="month"
              required
              :aria-invalid="Boolean(manualFieldError('period'))"
              class="rounded-lais border border-lais-border bg-white px-3 py-2 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
              @input="clearManualFieldError('period')"
            />
            <p
              v-if="manualFieldError('period')"
              class="text-lais-body-xs text-n-ruby-11"
            >
              {{ manualFieldError('period') }}
            </p>
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-lais-label-sm font-semibold text-lais-text-body">
              {{ t('COMMISSION_MANAGEMENT.MANUAL_MODAL.FIELDS.AGENT') }}
            </span>
            <select
              v-model="manualForm.agent_id"
              :aria-invalid="Boolean(manualFieldError('agent_id'))"
              class="rounded-lais border border-lais-border bg-white px-3 py-2 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
              @change="clearManualFieldError('agent_id', 'beneficiary')"
            >
              <option value="">
                {{ t('COMMISSION_MANAGEMENT.MANUAL_MODAL.FIELDS.AGENT_NONE') }}
              </option>
              <option
                v-for="opt in manualAgentOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <p
              v-if="manualFieldError('agent_id')"
              class="text-lais-body-xs text-n-ruby-11"
            >
              {{ manualFieldError('agent_id') }}
            </p>
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-lais-label-sm font-semibold text-lais-text-body">
              {{ t('COMMISSION_MANAGEMENT.MANUAL_MODAL.FIELDS.TEAM') }}
            </span>
            <select
              v-model="manualForm.team_id"
              :aria-invalid="Boolean(manualFieldError('team_id'))"
              class="rounded-lais border border-lais-border bg-white px-3 py-2 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
              @change="clearManualFieldError('team_id', 'beneficiary')"
            >
              <option value="">
                {{ t('COMMISSION_MANAGEMENT.MANUAL_MODAL.FIELDS.TEAM_NONE') }}
              </option>
              <option
                v-for="opt in manualTeamOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <p
              v-if="manualFieldError('team_id')"
              class="text-lais-body-xs text-n-ruby-11"
            >
              {{ manualFieldError('team_id') }}
            </p>
          </label>
          <p
            v-if="manualFieldError('beneficiary')"
            class="text-lais-body-xs text-n-ruby-11 sm:col-span-2"
          >
            {{ manualFieldError('beneficiary') }}
          </p>
          <label class="flex flex-col gap-1">
            <span class="text-lais-label-sm font-semibold text-lais-text-body">
              {{
                t('COMMISSION_MANAGEMENT.MANUAL_MODAL.FIELDS.BUSINESS_CONTEXT')
              }}
            </span>
            <select
              v-model="manualForm.business_context"
              :aria-invalid="Boolean(manualFieldError('business_context'))"
              class="rounded-lais border border-lais-border bg-white px-3 py-2 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
              @change="clearManualFieldError('business_context')"
            >
              <option value="">
                {{
                  t(
                    'COMMISSION_MANAGEMENT.MANUAL_MODAL.FIELDS.BUSINESS_CONTEXT_NONE'
                  )
                }}
              </option>
              <option
                v-for="ctx in MANUAL_BUSINESS_CONTEXTS"
                :key="ctx"
                :value="ctx"
              >
                {{
                  t(
                    `COMMISSION_MANAGEMENT.MANUAL_MODAL.BUSINESS_CONTEXTS.${ctx}`
                  )
                }}
              </option>
            </select>
            <p
              v-if="manualFieldError('business_context')"
              class="text-lais-body-xs text-n-ruby-11"
            >
              {{ manualFieldError('business_context') }}
            </p>
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-lais-label-sm font-semibold text-lais-text-body">
              {{ t('COMMISSION_MANAGEMENT.MANUAL_MODAL.FIELDS.BASE_AMOUNT') }}
            </span>
            <input
              v-model="manualForm.base_amount"
              type="number"
              min="0"
              step="0.01"
              inputmode="decimal"
              :aria-invalid="Boolean(manualFieldError('base_amount'))"
              class="rounded-lais border border-lais-border bg-white px-3 py-2 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
              @input="clearManualFieldError('base_amount')"
            />
            <p
              v-if="manualFieldError('base_amount')"
              class="text-lais-body-xs text-n-ruby-11"
            >
              {{ manualFieldError('base_amount') }}
            </p>
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-lais-label-sm font-semibold text-lais-text-body">
              {{ t('COMMISSION_MANAGEMENT.MANUAL_MODAL.FIELDS.PERCENTAGE') }}
            </span>
            <input
              v-model="manualForm.percentage"
              type="number"
              min="0"
              max="100"
              step="0.01"
              inputmode="decimal"
              :aria-invalid="Boolean(manualFieldError('percentage'))"
              class="rounded-lais border border-lais-border bg-white px-3 py-2 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
              @input="clearManualFieldError('percentage')"
            />
            <p
              v-if="manualFieldError('percentage')"
              class="text-lais-body-xs text-n-ruby-11"
            >
              {{ manualFieldError('percentage') }}
            </p>
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-lais-label-sm font-semibold text-lais-text-body">
              {{ t('COMMISSION_MANAGEMENT.MANUAL_MODAL.FIELDS.STATUS') }}
            </span>
            <div class="flex items-center gap-2">
              <select
                v-model="manualForm.status"
                :aria-invalid="Boolean(manualFieldError('status'))"
                class="min-w-0 flex-1 rounded-lais border border-lais-border bg-white px-3 py-2 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
                @change="clearManualFieldError('status')"
              >
                <option
                  v-for="opt in COMMISSION_STATUSES"
                  :key="opt"
                  :value="opt"
                >
                  {{ t(`COMMISSION_MANAGEMENT.STATUSES.${opt}`) }}
                </option>
              </select>
              <LaisStatusBadge
                :tone="manualStatusTone"
                :label="
                  t(`COMMISSION_MANAGEMENT.STATUSES.${manualForm.status}`)
                "
              />
            </div>
            <p
              v-if="manualFieldError('status')"
              class="text-lais-body-xs text-n-ruby-11"
            >
              {{ manualFieldError('status') }}
            </p>
          </label>
          <label class="flex flex-col gap-1 sm:col-span-2">
            <span class="text-lais-label-sm font-semibold text-lais-text-body">
              {{ t('COMMISSION_MANAGEMENT.MANUAL_MODAL.FIELDS.REASON') }}
            </span>
            <textarea
              v-model="manualForm.reason"
              rows="3"
              required
              :maxlength="MANUAL_MAX_REASON_LENGTH"
              :aria-invalid="Boolean(manualFieldError('reason'))"
              class="rounded-lais border border-lais-border bg-white px-3 py-2 text-lais-body-sm text-lais-text focus-visible:outline-none focus-visible:shadow-lais-focus"
              :placeholder="
                t(
                  'COMMISSION_MANAGEMENT.MANUAL_MODAL.FIELDS.REASON_PLACEHOLDER'
                )
              "
              @input="clearManualFieldError('reason')"
            />
            <p
              v-if="manualFieldError('reason')"
              class="text-lais-body-xs text-n-ruby-11"
            >
              {{ manualFieldError('reason') }}
            </p>
          </label>
        </div>

        <LaisMoneySnapshot
          :amount="manualValuePreview"
          currency="BRL"
          :label="t('COMMISSION_MANAGEMENT.MANUAL_MODAL.PREVIEW_LABEL')"
          :source="t('COMMISSION_MANAGEMENT.MANUAL_MODAL.PREVIEW_SOURCE')"
          :tone="isManualValid ? 'positive' : 'neutral'"
        />

        <p v-if="manualErrorMessage" class="text-lais-body-xs text-n-ruby-11">
          {{ manualErrorMessage }}
        </p>
        <p
          v-else-if="!isManualValid"
          class="text-lais-body-xs text-lais-text-helper"
        >
          {{ t('COMMISSION_MANAGEMENT.MANUAL_MODAL.VALIDATION') }}
        </p>
      </form>

      <template #footer>
        <div class="flex items-center justify-end gap-3">
          <LaisPrimaryButton
            variant="ghost"
            :disabled="isSubmittingManual"
            @click="closeManualModal"
          >
            {{ t('COMMISSION_MANAGEMENT.MANUAL_MODAL.CANCEL') }}
          </LaisPrimaryButton>
          <LaisPrimaryButton
            :is-loading="isSubmittingManual"
            :disabled="isSubmittingManual || isPeriodClosed"
            @click="submitManual"
          >
            {{ t('COMMISSION_MANAGEMENT.MANUAL_MODAL.SUBMIT') }}
          </LaisPrimaryButton>
        </div>
      </template>
    </LaisModal>

    <CommissionAgreementsPanel
      :show="showCommissionAgreements"
      :agents="accountAgents || []"
      :can-manage="canManage"
      :teams="accountTeams || []"
      @close="showCommissionAgreements = false"
    />
  </div>
</template>
